import { Disposable } from "../../../base/common/lifecycle.js";
import { ILogService } from "../../log/common/log.js";
import { combineNavigationDeviceIntent, emptyNavigationDeviceIntent, INavigationDeviceService, NavigationDeviceIntent, NavigationIntentOptions, parseGamepadIntent, parseKeyboardIntent, parseSpaceMouseIntent } from "../common/navigationDevice.js";
import { IUserConfiguration } from "../../../editor/services/user-configuration/common/userConfiguration.js";
import { BrowserWebHIDDeviceCollection } from "../../hid/browser/webHidDeviceCollection.js";
import { createSpaceMouseDeviceState, createSpaceMouseRequestFilters, isLikelySpaceMouseDevice, SpaceMouseDeviceState, updateSpaceMouseDeviceState } from "../../hid/common/spaceMouse.js";
import { getWebHIDApi } from "../../hid/common/webHid.js";
import { IKeyboardService } from "../../keyboard/common/keyboardService.js";
import { isEditableElementFocused } from "../../../base/browser/dom.js";

export class BrowserNavigationDeviceService extends Disposable implements INavigationDeviceService {
    private readonly spaceMouseDevices: BrowserWebHIDDeviceCollection<SpaceMouseDeviceState>;

    constructor(
        @ILogService private readonly logger: ILogService,
        @IUserConfiguration private readonly config: IUserConfiguration,
        @IKeyboardService private readonly keyboard: IKeyboardService,
    ) {
        super();

        // @TODO: this needs to use the IWindowsMainService/input service strictly speaking (since the inspector can change the active window which is handled by the input service)
        window.addEventListener("gamepadconnected", this.onGamepadConnectionChanged);
        window.addEventListener("gamepaddisconnected", this.onGamepadConnectionChanged);
        this._register({
            dispose: () => {
                window.removeEventListener("gamepadconnected", this.onGamepadConnectionChanged);
                window.removeEventListener("gamepaddisconnected", this.onGamepadConnectionChanged);
            }
        });


        // @TODO: see comment above: if the window changes this device needs to update too!
        this.spaceMouseDevices = this._register(new BrowserWebHIDDeviceCollection({
            hid: getWebHIDApi(navigator),
            requestOptions: {
                filters: createSpaceMouseRequestFilters(),
            },
            supportsDevice: isLikelySpaceMouseDevice,
            createState: () => createSpaceMouseDeviceState(),
            updateState: (state, event) => updateSpaceMouseDeviceState(state, { reportId: event.reportId, data: event.data }),
            onDeviceConnected: device => this.logger.info(`NavigationDeviceService: Connected HID navigation device '${device.productName ?? "unknown"}'.`),
            onDeviceDisconnected: device => this.logger.info(`NavigationDeviceService: HID device disconnected: ${device.productName ?? "unknown"}`),
            onError: (error, context) => this.logger.warn(`NavigationDeviceService: WebHID ${context.phase} failed${context.device ? ` for '${context.device.productName ?? "unknown"}'` : ""}.`, error),
        }));

        void this.spaceMouseDevices.restoreGrantedDevices();
    }

    getIntent(options?: NavigationIntentOptions): NavigationDeviceIntent {
        const intents = new Array<NavigationDeviceIntent>();
        const pads = navigator.getGamepads?.() ?? [];

        // Keyboard state is global (every window), so a caller that owns a viewport
        // in ONE window can ask to be driven only while THAT window has focus —
        // otherwise keys pressed in a popout fly the main window's camera.
        const keyboardWindow = options?.window;
        const keyboardHasFocus = !keyboardWindow || keyboardWindow.document.hasFocus();

        // Ignore keyboard navigation while the user is typing in a text field —
        // otherwise WASD/arrows in a search / property / AI-chat input fly the
        // camera. (Gamepad / SpaceMouse are unaffected.)
        if (this.config.cameraKeyboardMouseEnabled && keyboardHasFocus && !isEditableElementFocused()) {
            intents.push(parseKeyboardIntent(this.keyboard, { wasdQE: options?.wasdQE }));
        }
        if (this.config.cameraGamepadEnabled) {
            for (const pad of pads) {
                if (!pad) continue;
                intents.push(parseGamepadIntent(pad, this.config.cameraGamepadDeadzone));
            }
        }

        if (this.config.cameraSpaceMouseEnabled) {
            for (const state of this.spaceMouseDevices.getStates()) {
                intents.push(parseSpaceMouseIntent(state));
            }
        }

        return intents.length === 0 ? emptyNavigationDeviceIntent() : combineNavigationDeviceIntent(...intents);
    }

    async requestSpaceMouseDevice(): Promise<boolean> {
        if (!this.spaceMouseDevices.available) {
            this.logger.warn("NavigationDeviceService: WebHID is not available in this browser.");
            return false;
        }
        if (this.spaceMouseDevices.hasDevices) {
            return true;
        }
        return this.spaceMouseDevices.requestAccess();
    }

    private onGamepadConnectionChanged = (event: Event) => {
        const gamepadEvent = event as GamepadEvent;
        const action = event.type === "gamepadconnected" ? "connected" : "disconnected";
        this.logger.info(`NavigationDeviceService: Gamepad ${action}: ${gamepadEvent.gamepad?.id ?? "unknown"}`);
    };
}
